import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { FiZap } from "react-icons/fi";

import Axios from "../../../../_services/caller.service";
import Loader from "../../../UiComponents/loader/Loader";
import UserSearchSelect from "../userSearchSelect/UserSearchSelect";
import ModalConfirmInject from "../modal/ModalConfirmInject";
import PresetTest from "../presetTest/PresetTest";
import PresetCr from "../presetCr/PresetCr";
import styles from "./InjectPresets.module.scss";

/**
 * Onglet "Injection" : selection multiple d'utilisateurs (cases a cocher)
 * puis injection des presets par defaut (Test, CR ou les deux).
 * Si UN SEUL utilisateur est selectionne, l'edition manuelle de ses
 * presets reste disponible en dessous.
 *
 * Props :
 *  - allUser : liste des utilisateurs
 *  - setOverlay : setter de l'overlay parent
 */
const InjectPresets = ({ allUser, setOverlay }) => {
  const { t } = useTranslation();
  const [selectedIds, setSelectedIds] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingTypes, setPendingTypes] = useState([]);
  const [mode, setMode] = useState("replace");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const openInject = (types) => {
    if (selectedIds.length === 0) return;
    setPendingTypes(types);
    setMode("replace");
    setOverlay(true);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setOverlay(false);
  };

  const confirmInject = () => {
    setLoading(true);
    Axios.post("/injectDefaultPresets", {
      userIds: selectedIds,
      types: pendingTypes,
      mode,
    })
      .then((response) => {
        setLoading(false);
        setSuccess(true);
        setRefreshKey((k) => k + 1);
        toast.success(response.data?.message || t("preset.inject_success"));
        setTimeout(() => {
          setSuccess(false);
          closeModal();
        }, 1500);
      })
      .catch((error) => {
        setLoading(false);
        toast.error(
          error?.response?.data?.message || t("preset.inject_error")
        );
        closeModal();
      });
  };

  const singleUserId = selectedIds.length === 1 ? selectedIds[0] : null;

  return (
    <div className={styles.wrapper}>
      <div className={styles.selector}>
        <label className={styles.label}>{t("preset.select_users_label")}</label>
        <UserSearchSelect
          allUser={allUser}
          selectedIds={selectedIds}
          onChange={setSelectedIds}
          multiSelect
        />
        <span className={styles.count}>
          {t("preset.selected_count", { count: selectedIds.length })}
        </span>
      </div>

      <div className={styles.injectBar}>
        <button
          type="button"
          className={styles.btnGhost}
          disabled={selectedIds.length === 0}
          onClick={() => openInject(["test"])}
        >
          <FiZap /> {t("preset.inject_test")}
        </button>
        <button
          type="button"
          className={styles.btnGhost}
          disabled={selectedIds.length === 0}
          onClick={() => openInject(["cr"])}
        >
          <FiZap /> {t("preset.inject_cr")}
        </button>
        <button
          type="button"
          className={styles.btnPrimary}
          disabled={selectedIds.length === 0}
          onClick={() => openInject(["test", "cr"])}
        >
          <FiZap /> {t("preset.inject_both")}
        </button>
      </div>

      {singleUserId ? (
        <div className={styles.manualEdit}>
          <p className={styles.manualHint}>{t("preset.manual_edit_hint")}</p>
          <div className={styles.manualGrid}>
            <PresetTest
              key={`test-${singleUserId}-${refreshKey}`}
              userId={singleUserId}
              setOverlay={setOverlay}
            />
            <PresetCr
              key={`cr-${singleUserId}-${refreshKey}`}
              userId={singleUserId}
              setOverlay={setOverlay}
            />
          </div>
        </div>
      ) : selectedIds.length > 1 ? (
        <p className={styles.multiHint}>{t("preset.multi_selected_hint")}</p>
      ) : (
        <p className={styles.emptyHint}>{t("preset.select_user_for_presets")}</p>
      )}

      {modalOpen && (
        <ModalConfirmInject
          userCount={selectedIds.length}
          types={pendingTypes}
          mode={mode}
          setMode={setMode}
          onConfirm={confirmInject}
          onCancel={closeModal}
          success={success}
          loading={loading}
        />
      )}
      {loading && <Loader />}
    </div>
  );
};

export default InjectPresets;
